import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import type { FC } from 'react';
import React, { memo, useCallback, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import {
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_ALL_MACRO_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { routeNameToContentTypeMap } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import {
	getExperienceTracker,
	stopExperienceOnError,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { usePageState } from '@confluence/page-context';
import { ContentContainer } from '@confluence/content-container/entry-points/ContentContainer';
import { ContentUnifiedQuery, ContentBlogUnifiedQuery } from '@confluence/content-unified-query';
import type {
	ContentUnifiedQueryType,
	ContentBlogUnifiedQueryType,
	ContentUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper';
import { expValEquals } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { PagePrerequisites } from '@confluence/content-prerequisites/entry-points/PagePrerequisites';
import { useDocumentUpdateStatusDispatch } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext';
import {
	VIEW_PAGE,
	SPACE_OVERVIEW,
	COMPANY_HUB,
} from '@confluence/named-routes/entry-points/routes';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart';
import { ContentStateContainer } from '@confluence/content-state/entry-points/useContentState';
import {
	ReloadType,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData';

import type { BaseViewPageProps } from './BaseViewPageProps';
import { VIEW_PAGE_CONTAINER_METRIC } from './perf.config';
import { useContentExtensionMetrics } from './useContentExtensionMetrics';

const pageMap = {
	SPACE_OVERVIEW: {
		route: SPACE_OVERVIEW,
		errorAttribution: 'next-space-overview',
		name: SPACE_OVERVIEW_EXPERIENCE,
		isContentView: false,
	},
	COMPANY_HUB: {
		route: COMPANY_HUB,
		errorAttribution: 'next-company-hub',
		name: FRONT_COVER_LOAD_EXPERIENCE,
		isContentView: false,
	},
	page: {
		route: VIEW_PAGE,
		errorAttribution: 'next-view-page',
		name: VIEW_PAGE_EXPERIENCE,
		isContentView: true,
	},
};

type ContentQueryVariablesTimestamp = ContentUnifiedQueryVariablesType & {
	timestamp?: number;
};

interface ViewPageContainerProps extends BaseViewPageProps {
	children?: (props: {
		isThemed: boolean;
		contentQueryData: ContentUnifiedQueryType | ContentBlogUnifiedQueryType | undefined;
		contentQueryError: ApolloErrorInterop | undefined;
	}) => React.ReactNode | null;
	stickyHeader?: React.ReactNode;
	contentScreenStyles?: React.CSSProperties;
	isContainerContentView?: boolean;
	versionOverride?: number | null;
	customPerfData?: Record<string, string | boolean | object>;
}

export const ViewPageContainer: FC<ViewPageContainerProps> = memo(
	({
		contentId,
		spaceKey,
		children,
		stickyHeader,
		contentScreenStyles,
		isContainerContentView = true,
		versionOverride = null,
		customPerfData,
	}) => {
		const { setDocumentNeedsUpdate, setPublishedDocumentVersion } =
			useDocumentUpdateStatusDispatch();
		const [{ routeName = '', contentType }] = usePageState();
		const { edition } = useSessionData();
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

		const [{ orderedActiveAnnotationIdList, commentsDataMap }, { addNewCommentThreads }] =
			useCommentsData();
		useUnreadCommentsSetup({
			pageId: contentId,
			pageContentTypeProps: {
				pageMode: 'view',
				orderedActiveAnnotationIdList,
				commentsDataMap,
				addNewCommentThreads,
			},
		});

		const extractDataAndSetPublishedVersion = useCallback(
			({ refetchContentQueryResult }) => {
				const { data } = refetchContentQueryResult;
				if (data) {
					const versionNumber = data.content?.nodes?.[0]?.version?.number;
					if (versionNumber !== undefined && versionNumber !== null) {
						setPublishedDocumentVersion(versionNumber);
					}
				}
			},
			[setPublishedDocumentVersion],
		);

		let chooseContentUnifiedQuery = ContentUnifiedQuery;

		if (contentType === 'blogpost') {
			chooseContentUnifiedQuery = ContentBlogUnifiedQuery;
		}

		const contentQueryResult = useQuery<ContentUnifiedQueryType, ContentQueryVariablesTimestamp>(
			chooseContentUnifiedQuery,
			{
				fetchPolicy: 'cache-first',
				context: { single: true },
				skip: !contentId || !spaceKey,
				variables: {
					contentId,
					spaceKey,
					isSSR,
					versionOverride,
					useNewContentTopper: true,
				},
			},
		);

		const { route, errorAttribution, name, isContentView } = pageMap[routeName] ?? pageMap['page'];

		const { refetch } = contentQueryResult;

		useContentExtensionMetrics(
			contentQueryResult?.data?.content?.nodes?.[0]?.body?.dynamic?.value,
			customPerfData,
		);

		useEffect(() => {
			// If navigating to a different page, always assume document is up to date.
			// Added for COMMENTS-5327, so when user jumps from an outdated page to another
			// up-to-date page, we won't disable the comment button due to outdated-ness.
			// Caveat: The destination page may not actually be up-to-date, e.g. if the
			// page content was loaded from cache that's saved from the user's previous
			// visit in the same browser tab, and a new version was published since then.
			// Invoking `setDocumentNeedsUpdate(false)` here means `documentNeedsUpdate`
			// may be `false` when page is actually outdated, but this scenario can already
			// occur before this change. COMMENTS-5816 tracks fixing this scenario altogether.
			if (expValEquals('confluence_fe_disable_comment_if_page_outdated', 'isEnabled', true)) {
				setDocumentNeedsUpdate(false);
			}
		}, [contentId, setDocumentNeedsUpdate]);

		const reload = useCallback(
			async () => {
				getExperienceTracker().start({
					name,
					timeout: ExperienceTimeout.PAGE_LOAD,
					attributes: {
						contentType: routeNameToContentTypeMap[routeName],
						contentId,
						isSpaceOverviewNextRendered: true,
						edition,
						quickReload: true, //tell exp metrics error came from reload and not primary experience
					},
				});

				try {
					const refetchContentQueryResult = await refetch({
						contentId,
						timestamp: Date.now(),
					});

					extractDataAndSetPublishedVersion({ refetchContentQueryResult });
				} catch (error) {
					if (isUnauthorizedError(error) || isExpectedApolloError(error)) {
						markErrorAsHandled(error);
						getExperienceTracker().abort({
							name,
							reason: 'Expected Apollo error',
						});
						return; //if it's an expected GQL failure don't throw or fail the experience
					}
					stopExperienceOnError(name, error);
					throw error; //this successfully avoids the success below
				}
				getExperienceTracker().succeed({ name });
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[refetch, contentId],
		);

		useQuickReloadSubscriber({
			name: 'content',
			types: [ReloadType.content, ReloadType.comment],
			reload,
		});

		return (
			<>
				<PageSegmentLoadStart key={contentId} metric={VIEW_PAGE_CONTAINER_METRIC} />
				<MacroExperienceStart
					contentId={contentId}
					mode={RENDERER}
					name={VIEW_ALL_MACRO_EXPERIENCE}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				<PagePrerequisites
					route={route}
					contentQueryResult={contentQueryResult}
					versionOverride={versionOverride}
				>
					<ContentStateContainer scope={contentId}>
						<ContentContainer
							fetchTheme
							contentId={contentId}
							spaceKey={spaceKey}
							key="content-screen"
							errorAttribution={errorAttribution}
							isScreenContentView={isContentView}
							isContainerContentView={isContainerContentView}
							contentScreenStyles={contentScreenStyles}
							stickyHeader={stickyHeader}
							versionOverride={versionOverride}
						>
							{({ isThemed }) => (
								<>
									{children &&
										children({
											isThemed,
											contentQueryData: contentQueryResult.data,
											contentQueryError: contentQueryResult.error,
										})}
									<PageSegmentLoadEnd
										key={contentId}
										metric={VIEW_PAGE_CONTAINER_METRIC}
										customData={{ isThemed }}
									/>
								</>
							)}
						</ContentContainer>
					</ContentStateContainer>
				</PagePrerequisites>
			</>
		);
	},
);
